import { useCallback, useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { EventName } from ':cloud/init/clientAnalytics/logging';
import { CodeBlockLanguage, PaymasterLogItem } from ':cloud/types/ts_types';
import { PAYMASTER_USER_OP_DEV_LINK } from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';

import { formatLogDate } from '../helpers';
import { CodeBlock } from '../sharedcomponents';

import { BUNDLER_ERROR_MAP, PAYMASTER_ERROR_MAP } from './constants';

const customCodeblockStyle = { fontSize: '12px', padding: '12px' };

const METHODS_WITH_SIMULATOR = ['eth_sendUserOperation', 'eth_estimateUserOperationGas'];

type PaymasterLogDetailProps = {
  log: PaymasterLogItem;
  selectedLogIndex: number;
  setSelectedLogIndex: (index: number) => void;
  maxLogIndex: number;
  maxHeight?: number | string;
};

type LogDetailItemProps = {
  label: string;
  value: string | number;
};

function LogDetailItem({ label, value }: LogDetailItemProps) {
  return (
    <HStack
      justifyContent="space-between"
      spacingHorizontal={3}
      spacingVertical={1}
      flexWrap="wrap"
    >
      <TextBody as="label" color="foregroundMuted">
        {label}
      </TextBody>
      <TextHeadline as="p">{value}</TextHeadline>
    </HStack>
  );
}
export function PaymasterLogDetail({
  log,
  setSelectedLogIndex,
  selectedLogIndex,
  maxLogIndex,
  maxHeight,
}: PaymasterLogDetailProps) {
  const error = useMemo(() => {
    if (!log?.errorCode) {
      return '';
    }
    if (log?.service === 'Paymaster') {
      return PAYMASTER_ERROR_MAP[log.errorCode];
    }
    return BUNDLER_ERROR_MAP[log.errorCode];
  }, [log]);

  const handleArrowPress = useCallback(
    (direction: 'down' | 'up') => {
      return () => {
        if (direction === 'down') {
          setSelectedLogIndex(
            selectedLogIndex < maxLogIndex - 1 ? selectedLogIndex + 1 : maxLogIndex - 1,
          );
        } else {
          setSelectedLogIndex(selectedLogIndex > 0 ? selectedLogIndex - 1 : 0);
        }
      };
    },
    [maxLogIndex, selectedLogIndex, setSelectedLogIndex],
  );

  const formattedJson = useMemo(() => {
    if (!log?.rpcJson) {
      return '';
    }
    return JSON.stringify(JSON.parse(log?.rpcJson), null, 2);
  }, [log?.rpcJson]);

  const rpcCode = useMemo(() => {
    return [
      {
        language: 'bash' as CodeBlockLanguage,
        code: formattedJson,
      },
    ];
  }, [formattedJson]);

  const showSimulator = log?.method && METHODS_WITH_SIMULATOR.includes(log.method);

  return (
    <VStack
      borderedStart
      flexGrow={1}
      width={440}
      maxWidth={440}
      height={maxHeight}
      overflow="scroll"
    >
      <HStack borderedBottom spacing={3} justifyContent="space-between">
        <HStack alignItems="center" gap={1}>
          <Icon name="circleCross" color="negative" size="s" />
          <TextHeadline as="h3">{log.method}</TextHeadline>
        </HStack>
        <HStack gap={2}>
          <Pressable background="transparent" onPress={handleArrowPress('down')}>
            <Icon name="caretDown" size="m" color="foreground" />
          </Pressable>
          <Pressable background="transparent" onPress={handleArrowPress('up')}>
            <Icon name="caretUp" size="m" color="foreground" />
          </Pressable>
        </HStack>
      </HStack>
      <VStack spacingVertical={2}>
        <LogDetailItem label="Created at" value={formatLogDate(log.createdAt)} />
        <LogDetailItem label="Method" value={log?.method || ''} />
        <HStack
          justifyContent="space-between"
          spacingHorizontal={3}
          spacingVertical={1}
          flexWrap="wrap"
        >
          <TextBody as="label" color="foregroundMuted">
            Error
          </TextBody>
          <HStack alignItems="center" gap={0.5}>
            {error && <Icon name="dot" color="negative" size="s" />}
            <TextBody as="p" color="foregroundMuted">
              {error}
            </TextBody>
          </HStack>
        </HStack>
        <LogDetailItem label="Error code" value={log?.errorCode || ''} />
        <LogDetailItem label="Error message" value={log?.errorMessage || ''} />
      </VStack>

      {formattedJson && (
        <Box spacing={2} maxWidth="100%" flexShrink={1} overflow="hidden">
          <CodeBlock
            title="Request"
            showCopy
            showLanguageSelect={false}
            enableLineWrap
            codeArr={rpcCode}
            showLineNumbers={false}
            customStyle={customCodeblockStyle}
            titleSpacingStart={2}
            titleSpacingEnd={2}
            titleSpacingTop={2}
            height="100%"
            overflow="scroll"
            copyEventName={EventName.paymaster_logs_copy_rpc_click}
          />
        </Box>
      )}

      {showSimulator && (
        <Box spacingBottom={6} spacingTop={1} spacingHorizontal={2} display="block">
          <Button
            variant="secondary"
            compact
            block
            onPress={onExternalLinkPress(PAYMASTER_USER_OP_DEV_LINK)}
          >
            <HStack alignItems="center" justifyContent="center" gap={2}>
              <TextHeadline as="p">View simulation</TextHeadline>
              <Icon name="diagonalUpArrow" size="s" color="foreground" />
            </HStack>
          </Button>
        </Box>
      )}
    </VStack>
  );
}
